import axios from '@/axios-client.js';

// =====================================================
// Helper function to add any image to PDF
// Handles logos (contain) and photos (cover)
// =====================================================
export function addImageToPdf(
  pdf,
  img,               // HTMLImageElement or canvas
  x,
  y,
  boxW,
  boxH,
  {
    mode = 'contain', // 'contain' = logo/map, 'cover' = photo
    maxSize = 512,    // max canvas size
    quality = 0.85,   // JPEG quality
    fillWhite = true  // fill background with white
  } = {}
) {
  // ----- Calculate resize -----
  let targetW = img.width;
  let targetH = img.height;

  if (img.width > img.height) {
    targetW = Math.min(img.width, maxSize);
    targetH = targetW / (img.width / img.height);
  } else {
    targetH = Math.min(img.height, maxSize);
    targetW = targetH * (img.width / img.height);
  }

  // ----- Canvas -----
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(targetW);
  canvas.height = Math.round(targetH);

  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Fill white if necessary
  if (fillWhite) {
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  // ----- Crop or center -----
  let sx = 0, sy = 0, sw = img.width, sh = img.height;

  const imgRatio = img.width / img.height;
  const boxRatio = boxW / boxH;

  if (mode === 'cover') {
    if (imgRatio > boxRatio) {
      // Landscape → crop horizontal
      sh = img.height;
      sw = sh * boxRatio;
      sx = (img.width - sw) / 2;
    } else {
      // Portrait → crop vertical
      sw = img.width;
      sh = sw / boxRatio;
      sy = (img.height - sh) / 2;
    }
  }

  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

  // ----- Calculate placement for 'contain' mode -----
  let drawW = boxW, drawH = boxH;
  let drawX = x, drawY = y;

  if (mode === 'contain') {
    const canvasRatio = canvas.width / canvas.height;
    if (canvasRatio > boxRatio) {
      drawW = boxW;
      drawH = boxW / canvasRatio;
    } else {
      drawH = boxH;
      drawW = boxH * canvasRatio;
    }
    drawX = x + (boxW - drawW) / 2;
    drawY = y + (boxH - drawH) / 2;
  }

  // ----- Export format depending on mode -----
  let dataUrl;
  let format;

  if (mode === 'cover') {
    // Photos → PNG to preserve colors and sharpness
    dataUrl = canvas.toDataURL('image/png');
    format = 'PNG';
  } else {
    // Logo / map → JPEG to reduce size
    dataUrl = canvas.toDataURL('image/jpeg', quality);
    format = 'JPEG';
  }

  pdf.addImage(dataUrl, format, drawX, drawY, drawW, drawH);
}

// =====================================================
// Load an image from URL and return an optimized canvas
// =====================================================
export async function loadImage(url) {
  try {
    const response = await axios.get(url, { responseType: 'blob' });

    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(response.data);
      img.onload = () => resolve(img);
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error(`Image could not be decoded: ${url}`));
      };
      img.src = objectUrl;
    });
  } catch (error) {
    console.error('Error loading image:', error);
    throw error;
  }
}

// =====================================================
// Convert hex color to RGB array
// =====================================================
export function hexToRgb(hex) {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;

  return [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16),
  ];
}

// =====================================================
// Normalize text: remove line breaks and extra spaces
// =====================================================
function normalizeText(text) {
  return String(text)
    .replace(/\r\n|\r|\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const PT_TO_MM = 0.3528;
const LINE_HEIGHT_FACTOR = 1.15;
const ATTR_FONT_STANDARD = 11;
const ATTR_FONT_COMPACT = 8;
const ATTR_LINE_LIMIT = 3;

// =====================================================
// Fit text in a box: reduce font size first, then wrap on several lines,
// and only ellipsize the last line if it still doesn't fit at min font size
// =====================================================
function ellipsizeLine(pdf, line, maxWidth, forceEllipsis = false) {
  const ellipsis = '…';
  let result = line || '';
  if (!forceEllipsis && pdf.getTextWidth(result) <= maxWidth) return result;
  while (result.length > 0 && pdf.getTextWidth(result + ellipsis) > maxWidth) {
    result = result.slice(0, -1);
  }
  return result + ellipsis;
}

function fitText(pdf, text, maxWidth, { maxFontSize, minFontSize, maxLines }) {
  const normalized = text ? normalizeText(text) : '';
  const lineHeightFor = (size) => size * PT_TO_MM * LINE_HEIGHT_FACTOR;
  const clampLines = (lines) => lines.map((line) => (
    pdf.getTextWidth(line) > maxWidth ? ellipsizeLine(pdf, line, maxWidth) : line
  ));

  for (let size = maxFontSize; size >= minFontSize; size -= 0.5) {
    pdf.setFontSize(size);
    const lines = normalized ? pdf.splitTextToSize(normalized, maxWidth) : [''];
    if (lines.length <= maxLines) {
      return { lines: clampLines(lines), fontSize: size, lineHeight: lineHeightFor(size) };
    }
  }

  pdf.setFontSize(minFontSize);
  const allLines = normalized ? pdf.splitTextToSize(normalized, maxWidth) : [''];
  const lines = clampLines(allLines.slice(0, maxLines));
  if (allLines.length > maxLines) {
    lines[maxLines - 1] = ellipsizeLine(pdf, lines[maxLines - 1], maxWidth, true);
  }
  return { lines, fontSize: minFontSize, lineHeight: lineHeightFor(minFontSize) };
}

function lineHeightFor(fontSize) {
  return fontSize * PT_TO_MM * LINE_HEIGHT_FACTOR;
}

// Line count at a given size, using the real cell width (jsPDF wraps on that width).
function wrappedLines(pdf, text, maxWidth, fontSize, fontStyle) {
  pdf.setFont(undefined, fontStyle);
  pdf.setFontSize(fontSize);
  const normalized = text ? normalizeText(text) : '';
  if (!normalized) return [''];
  return pdf.splitTextToSize(normalized, maxWidth);
}

function capWrappedLines(pdf, lines, maxLines, maxWidth) {
  if (lines.length <= maxLines) return lines;
  const capped = lines.slice(0, maxLines);
  capped[maxLines - 1] = ellipsizeLine(pdf, capped[maxLines - 1], maxWidth, true);
  return capped;
}

function fitFixedLines(pdf, text, maxWidth, fontSize, maxLines, fontStyle) {
  const lines = wrappedLines(pdf, text, maxWidth, fontSize, fontStyle);
  pdf.setFont(undefined, fontStyle);
  pdf.setFontSize(fontSize);
  return fittedBlock(capWrappedLines(pdf, lines, maxLines, maxWidth), fontSize);
}

// One size for the whole group: standard unless any item exceeds 3 lines at that size.
function groupFontSize(pdf, texts, maxWidth, fontStyle) {
  const overflows = texts.some((text) => (
    wrappedLines(pdf, text, maxWidth, ATTR_FONT_STANDARD, fontStyle).length > ATTR_LINE_LIMIT
  ));
  return overflows ? ATTR_FONT_COMPACT : ATTR_FONT_STANDARD;
}

function fittedBlock(lines, fontSize) {
  return { lines, fontSize, lineHeight: lineHeightFor(fontSize) };
}

// Draw a fitted text block; y is the baseline of the first line
function drawFittedText(pdf, fitted, x, y, options = {}) {
  pdf.setFontSize(fitted.fontSize);
  pdf.text(fitted.lines, x, y, { lineHeightFactor: LINE_HEIGHT_FACTOR, ...options });
  return y + (fitted.lines.length - 1) * fitted.lineHeight;
}

// =====================================================
// Generate a clean, readable filename for the PDF export
// =====================================================
export function generatePdfFilename({
  featuresCountInFile,
  featureTitle,
  projectTitle,
  featureTypeTitle,
  batchIndex = null,
  totalBatches = null,
}) {
  // Clean string for filenames (remove accents, special chars, keep letters/numbers)
  const clean = (str = '') => {
    str = str.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')  // remove accents
      .replace(/[^a-z0-9]/gi, '_');     // replace non-alphanum with underscore

    // trim leading underscores
    let start = 0;
    while (str[start] === '_') start++;
    
    // trim trailing underscores
    let end = str.length;
    while (str[end - 1] === '_') end--;

    return str.slice(start, end);
  };

  // Capitalize first letter
  const capitalize = (str) => str.replace(/\b\w/g, (c) => c.toUpperCase());

  const nameParts = [];

  // Project title is always included
  if (projectTitle) nameParts.push(capitalize(clean(projectTitle)));

  // Feature type title is always included
  if (featureTypeTitle) {
    nameParts.push(capitalize(clean(featureTypeTitle)));
  }
  // Single feature in the file → include its title
  if (featuresCountInFile === 1 && featureTitle) {
    nameParts.push(capitalize(clean(featureTitle)));
  }

  // Only include lot info if multiple features in file AND multiple batches
  if (featuresCountInFile > 1 && totalBatches && totalBatches > 1 && batchIndex != null) {
    nameParts.push(`Part_${batchIndex}-${totalBatches}`);
  }

  return nameParts.join('-') + '.pdf';
}

// Fit attribute rows into a given height (shared by both page layouts).
// Fonts are chosen per group; rows are only shortened if the block still overflows.
function layoutAttributeRows(pdf, attributes, { keyMaxWidth, valueMaxWidth, maxHeight, rowPadding }) {
  const keyFontSize = groupFontSize(pdf, attributes.map((attr) => attr.label), keyMaxWidth, 'bold');
  const valueFontSize = groupFontSize(pdf, attributes.map((attr) => attr.value), valueMaxWidth, 'normal');

  const rowTextHeight = (attr) => Math.max(
    attr.keyFitted.lines.length * attr.keyFitted.lineHeight,
    attr.valueFitted.lines.length * attr.valueFitted.lineHeight
  );

  const rows = attributes.map((attr) => {
    const row = {
      keyFitted: fittedBlock(wrappedLines(pdf, attr.label, keyMaxWidth, keyFontSize, 'bold'), keyFontSize),
      valueFitted: fittedBlock(
        wrappedLines(pdf, attr.value, valueMaxWidth, valueFontSize, 'normal'),
        valueFontSize
      ),
    };
    row.rowHeight = rowTextHeight(row) + rowPadding;
    return row;
  });

  const trimSide = (fitted, fontStyle, fontSize, maxWidth) => {
    if (fitted.lines.length <= 1) return;
    pdf.setFont(undefined, fontStyle);
    pdf.setFontSize(fontSize);
    fitted.lines.pop();
    const last = fitted.lines.length - 1;
    fitted.lines[last] = ellipsizeLine(pdf, fitted.lines[last], maxWidth, true);
  };

  while (rows.length && rows.reduce((sum, row) => sum + row.rowHeight, 0) > maxHeight) {
    const longest = rows.reduce((best, row) => (row.rowHeight > best.rowHeight ? row : best));
    const before = longest.rowHeight;
    const valueHeight = longest.valueFitted.lines.length * longest.valueFitted.lineHeight;
    const keyHeight = longest.keyFitted.lines.length * longest.keyFitted.lineHeight;
    if (valueHeight >= keyHeight) {
      trimSide(longest.valueFitted, 'normal', valueFontSize, valueMaxWidth);
    }
    if (keyHeight >= valueHeight) {
      trimSide(longest.keyFitted, 'bold', keyFontSize, keyMaxWidth);
    }
    longest.rowHeight = rowTextHeight(longest) + rowPadding;
    if (longest.rowHeight >= before) break;
  }

  return { rows, keyFontSize };
}

function blockBaseline(rowTop, rowHeight, fitted) {
  const blockHeight = fitted.lines.length * fitted.lineHeight;
  return rowTop + (rowHeight - blockHeight) / 2 + fitted.fontSize * PT_TO_MM * 0.8;
}

// =====================================================
// Build PDF page matrix (logo, title, attributes, map, photos)
// =====================================================
export function buildPdfMatrix(pdf, data) {
  if (data.orientation === 'landscape') {
    buildLandscapeMatrix(pdf, data);
  } else {
    buildPortraitMatrix(pdf, data);
  }
}

const LANDSCAPE = {
  pageWidth: 297,
  pageHeight: 210,
  margin: 10,
  navy: [31, 42, 92],
  text: [70, 76, 92],
  muted: [110, 116, 130],
  panelFill: [236, 240, 247],
  panelBorder: [214, 220, 232],
  rowLine: [222, 226, 234],
  panelPadding: 4,
  gap: 5,
  mapWidth: 92,
  photoBlockHeight: 62,
  photoHeight: 44,
  footerHeight: 8,
  keyRatio: 0.28,
  keyMaxLines: 2,
};

function drawFrame(pdf, x, y, w, h) {
  const L = LANDSCAPE;
  pdf.setDrawColor(...L.panelBorder);
  pdf.setLineWidth(0.3);
  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(x, y, w, h, 1.5, 1.5, 'FD');
}

function buildLandscapeMatrix(pdf, {
  ftTitle,
  title,
  createdDate,
  attributes,
  mapCanvas,
  photos = [],
  themeColor,
  logoImg,
  index,
  total,
}) {
  const L = LANDSCAPE;
  const left = L.margin;
  const width = L.pageWidth - 2 * L.margin;
  const accent = themeColor || L.navy;

  // ===== HEADER: title + subtitle on the left, logo on the right =====
  const logoW = 40;
  const logoH = 16;
  if (logoImg) {
    addImageToPdf(pdf, logoImg, left + width - logoW, 8, logoW, logoH, { mode: 'contain', quality: 0.9 });
  }
  const headerTextWidth = width - logoW - 6;
  pdf.setTextColor(...L.navy);
  const titleFitted = fitFixedLines(pdf, title, headerTextWidth, 18, 3, 'bold');
  let headerEnd = drawFittedText(pdf, titleFitted, left, 16);

  pdf.setTextColor(...L.muted);
  if (ftTitle) {
    const typeFitted = fitFixedLines(pdf, ftTitle, headerTextWidth, 9, 4, 'normal');
    headerEnd = drawFittedText(pdf, typeFitted, left, headerEnd + 5);
  }

  pdf.setFont(undefined, 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...L.muted);
  const subtitle = [`Signalement ${index + 1} / ${total}`, `Créé le : ${createdDate}`]
    .filter(Boolean)
    .join('   ·   ');
  const subtitleY = headerEnd + 5;
  pdf.text(ellipsizeLine(pdf, subtitle, headerTextWidth), left, subtitleY);

  const ruleY = Math.max(subtitleY + 4, 8 + logoH + 3);
  pdf.setDrawColor(...accent);
  pdf.setLineWidth(0.8);
  pdf.line(left, ruleY, left + width, ruleY);

  // ===== VERTICAL ZONES =====
  const footerTop = L.pageHeight - 6 - L.footerHeight;
  const hasPhotos = photos.length > 0;
  const photoTop = footerTop - L.gap - L.photoBlockHeight;
  const bodyTop = ruleY + L.gap;
  const bodyBottom = hasPhotos ? photoTop - L.gap : footerTop - L.gap;
  const bodyHeight = bodyBottom - bodyTop;

  // ===== INFORMATIONS DU SIGNALEMENT =====
  const infoWidth = mapCanvas ? width - L.mapWidth - L.gap : width;
  const innerX = left + L.panelPadding;
  const innerWidth = infoWidth - 2 * L.panelPadding;
  const columnGap = 4;

  // Short labels keep the narrow label column; a long one switches labels and values to 50/50
  const narrowKey = innerWidth * L.keyRatio;
  const hasLongLabel = attributes.some((attr) => (
    wrappedLines(pdf, attr.label, narrowKey - columnGap, ATTR_FONT_STANDARD, 'bold').length > L.keyMaxLines
  ));
  const keyColumn = hasLongLabel ? innerWidth / 2 : narrowKey;
  const keyMaxWidth = keyColumn - columnGap;
  const valueX = innerX + keyColumn;
  const valueMaxWidth = innerWidth - keyColumn;

  const rowPadding = 4;
  const rowsTop = bodyTop + L.panelPadding;
  const { rows } = layoutAttributeRows(pdf, attributes, {
    keyMaxWidth,
    valueMaxWidth,
    maxHeight: bodyBottom - rowsTop - L.panelPadding,
    rowPadding,
  });

  const rowsHeight = rows.reduce((sum, row) => sum + row.rowHeight, 0);
  const minPanelHeight = mapCanvas ? 72 : 30;
  const infoHeight = Math.min(bodyHeight, Math.max(rowsHeight + 2 * L.panelPadding, minPanelHeight));
  drawFrame(pdf, left, bodyTop, infoWidth, infoHeight);

  let rowTop = rowsTop;
  rows.forEach((row, i) => {
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(...L.navy);
    drawFittedText(pdf, row.keyFitted, innerX, blockBaseline(rowTop, row.rowHeight, row.keyFitted));

    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(...L.text);
    drawFittedText(pdf, row.valueFitted, valueX, blockBaseline(rowTop, row.rowHeight, row.valueFitted));

    rowTop += row.rowHeight;
    if (i < rows.length - 1) {
      pdf.setDrawColor(...L.rowLine);
      pdf.setLineWidth(0.3);
      pdf.line(innerX, rowTop, innerX + innerWidth, rowTop);
    }
  });

  // ===== LOCALISATION DU SIGNALEMENT =====
  if (mapCanvas) {
    const mapX = left + infoWidth + L.gap;
    const mapPanelHeight = infoHeight;
    drawFrame(pdf, mapX, bodyTop, L.mapWidth, mapPanelHeight);
    const mapBoxW = L.mapWidth - 2;
    const mapBoxH = mapPanelHeight - 2;
    addImageToPdf(pdf, mapCanvas, mapX + 1, bodyTop + 1, mapBoxW, mapBoxH, {
      mode: 'cover',
      maxSize: 1024,
    });
  }

  // ===== PHOTOS DU SIGNALEMENT =====
  if (hasPhotos) {
    drawFrame(pdf, left, photoTop, width, L.photoBlockHeight);
    const photoGap = 6;
    const photoInnerWidth = width - 2 * L.panelPadding;
    const photoWidth = (photoInnerWidth - 2 * photoGap) / 3;
    const photoY = photoTop + L.panelPadding;
    photos.slice(0, 3).forEach((img, i) => {
      const x = left + L.panelPadding + i * (photoWidth + photoGap);
      addImageToPdf(pdf, img, x, photoY, photoWidth, L.photoHeight, { mode: 'cover', maxSize: 1024 });
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(7);
      pdf.setTextColor(...L.muted);
      pdf.text(`Photo ${i + 1}`, x, photoY + L.photoHeight + 4);
    });
  }

  // ===== FOOTER =====
  pdf.setFillColor(...L.panelFill);
  pdf.roundedRect(left, footerTop, width, L.footerHeight, 1.5, 1.5, 'F');
  pdf.setFont(undefined, 'normal');
  pdf.setFontSize(7);
  pdf.setTextColor(...L.muted);
  pdf.text(
    `Document de synthèse   ·   Page ${index + 1}/${total}`,
    left + width / 2,
    footerTop + L.footerHeight / 2 + 1.2,
    { align: 'center' }
  );
}

const PORTRAIT = {
  pageWidth: 210,
  pageHeight: 297,
  margin: 10,
  navy: LANDSCAPE.navy,
  text: LANDSCAPE.text,
  muted: LANDSCAPE.muted,
  panelBorder: LANDSCAPE.panelBorder,
  panelFill: LANDSCAPE.panelFill,
  rowLine: LANDSCAPE.rowLine,
  panelPadding: 4,
  gap: 5,
  mapHeight: 72,
  photoBlockHeight: 58,
  photoHeight: 42,
  footerHeight: 8,
  keyRatio: 0.28,
  keyMaxLines: 2,
};

function paintAttributeRows(pdf, rows, { innerX, valueX, rowTop, innerWidth, colors }) {
  let top = rowTop;
  rows.forEach((row, i) => {
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(...colors.navy);
    drawFittedText(pdf, row.keyFitted, innerX, blockBaseline(top, row.rowHeight, row.keyFitted));

    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(...colors.text);
    drawFittedText(pdf, row.valueFitted, valueX, blockBaseline(top, row.rowHeight, row.valueFitted));

    top += row.rowHeight;
    if (i < rows.length - 1) {
      pdf.setDrawColor(...colors.rowLine);
      pdf.setLineWidth(0.3);
      pdf.line(innerX, top, innerX + innerWidth, top);
    }
  });
}

function paintPhotos(pdf, photos, x, y, width, blockHeight, photoHeight, colors) {
  drawFrame(pdf, x, y, width, blockHeight);
  const photoGap = 6;
  const pad = colors.panelPadding;
  const photoWidth = (width - 2 * pad - 2 * photoGap) / 3;
  const photoY = y + pad;
  photos.slice(0, 3).forEach((img, i) => {
    const photoX = x + pad + i * (photoWidth + photoGap);
    addImageToPdf(pdf, img, photoX, photoY, photoWidth, photoHeight, { mode: 'cover', maxSize: 1024 });
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(...colors.muted);
    pdf.text(`Photo ${i + 1}`, photoX, photoY + photoHeight + 4);
  });
}

function drawDocumentHeader(pdf, layout, {
  ftTitle,
  title,
  createdDate,
  themeColor,
  logoImg,
  index,
  total,
}) {
  const left = layout.margin;
  const width = layout.pageWidth - 2 * layout.margin;
  const accent = themeColor || layout.navy;
  const logoW = 40;
  const logoH = 16;

  if (logoImg) {
    addImageToPdf(pdf, logoImg, left + width - logoW, 8, logoW, logoH, { mode: 'contain', quality: 0.9 });
  }
  const headerTextWidth = width - logoW - 6;
  pdf.setTextColor(...layout.navy);
  const titleFitted = fitFixedLines(pdf, title, headerTextWidth, 16, 3, 'bold');
  let headerEnd = drawFittedText(pdf, titleFitted, left, 14);

  pdf.setTextColor(...layout.muted);
  if (ftTitle) {
    const typeFitted = fitFixedLines(pdf, ftTitle, headerTextWidth, 9, 4, 'normal');
    headerEnd = drawFittedText(pdf, typeFitted, left, headerEnd + 5);
  }

  pdf.setFont(undefined, 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...layout.muted);
  const subtitle = [`Signalement ${index + 1} / ${total}`, `Créé le : ${createdDate}`]
    .filter(Boolean)
    .join('   ·   ');
  const subtitleY = headerEnd + 5;
  pdf.text(ellipsizeLine(pdf, subtitle, headerTextWidth), left, subtitleY);

  const ruleY = Math.max(subtitleY + 4, 8 + logoH + 3);
  pdf.setDrawColor(...accent);
  pdf.setLineWidth(0.8);
  pdf.line(left, ruleY, left + width, ruleY);
  return ruleY;
}

function drawDocumentFooter(pdf, layout, index, total) {
  const left = layout.margin;
  const width = layout.pageWidth - 2 * layout.margin;
  const footerTop = layout.pageHeight - 6 - layout.footerHeight;
  pdf.setFillColor(...layout.panelFill);
  pdf.roundedRect(left, footerTop, width, layout.footerHeight, 1.5, 1.5, 'F');
  pdf.setFont(undefined, 'normal');
  pdf.setFontSize(7);
  pdf.setTextColor(...layout.muted);
  pdf.text(
    `Document de synthèse   ·   Page ${index + 1}/${total}`,
    left + width / 2,
    footerTop + layout.footerHeight / 2 + 1.2,
    { align: 'center' }
  );
  return footerTop;
}

function buildPortraitMatrix(pdf, data) {
  const P = PORTRAIT;
  const {
    attributes,
    mapCanvas,
    photos = [],
    index,
    total,
  } = data;
  const left = P.margin;
  const width = P.pageWidth - 2 * P.margin;
  const ruleY = drawDocumentHeader(pdf, P, data);
  const footerTop = drawDocumentFooter(pdf, P, index, total);

  const hasPhotos = photos.length > 0;
  const hasMap = !!mapCanvas;
  let cursor = ruleY + P.gap;
  const bottomLimit = footerTop - P.gap;
  const below = (hasMap ? P.mapHeight + P.gap : 0) + (hasPhotos ? P.photoBlockHeight + P.gap : 0);
  const infoMaxHeight = Math.max(28, bottomLimit - cursor - below);

  const innerX = left + P.panelPadding;
  const innerWidth = width - 2 * P.panelPadding;
  const columnGap = 4;
  const narrowKey = innerWidth * P.keyRatio;
  const hasLongLabel = attributes.some((attr) => (
    wrappedLines(pdf, attr.label, narrowKey - columnGap, ATTR_FONT_STANDARD, 'bold').length > P.keyMaxLines
  ));
  const keyColumn = hasLongLabel ? innerWidth / 2 : narrowKey;
  const valueX = innerX + keyColumn;

  const rowPadding = 4;
  const { rows } = layoutAttributeRows(pdf, attributes, {
    keyMaxWidth: keyColumn - columnGap,
    valueMaxWidth: innerWidth - keyColumn,
    maxHeight: infoMaxHeight - 2 * P.panelPadding,
    rowPadding,
  });
  const rowsHeight = rows.reduce((sum, row) => sum + row.rowHeight, 0);
  const infoHeight = Math.min(infoMaxHeight, Math.max(rowsHeight + 2 * P.panelPadding, 28));
  drawFrame(pdf, left, cursor, width, infoHeight);
  paintAttributeRows(pdf, rows, {
    innerX,
    valueX,
    rowTop: cursor + P.panelPadding,
    innerWidth,
    colors: P,
  });
  cursor += infoHeight + P.gap;

  if (hasMap) {
    const reservedPhotos = hasPhotos ? P.photoBlockHeight + P.gap : 0;
    const mapHeight = Math.max(36, Math.min(P.mapHeight, bottomLimit - cursor - reservedPhotos));
    drawFrame(pdf, left, cursor, width, mapHeight);
    addImageToPdf(pdf, mapCanvas, left + 1, cursor + 1, width - 2, mapHeight - 2, {
      mode: 'cover',
      maxSize: 1024,
    });
    cursor += mapHeight + P.gap;
  }

  if (hasPhotos) {
    const photoBlockHeight = Math.max(36, Math.min(P.photoBlockHeight, bottomLimit - cursor));
    const photoHeight = Math.min(P.photoHeight, photoBlockHeight - P.panelPadding - 6);
    paintPhotos(pdf, photos, left, cursor, width, photoBlockHeight, photoHeight, P);
  }
}
